import { HTMLAttributes, ReactNode, ReactElement, useState } from "react";
import {
  useFloating,
  offset,
  shift,
  size,
  autoUpdate,
  useClick,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  flip,
  type Placement as FUPlacement,
} from "@floating-ui/react";

import * as S from "./styled";
import { EventType, Placement } from "./types";

export type OverlayRender = (api: { close: () => void }) => ReactNode;

export type PopoverProps = Omit<HTMLAttributes<HTMLDivElement>, "content"> & {
  /** Type of event to trigger the overlay display. */
  event?: EventType;
  /** Placement of the overlay content relative to the target. */
  placement: Placement;
  /** Target element for overlay to be bound to. */
  target: ReactNode;
  /** Content to display in the overlay. */
  content: ReactNode | OverlayRender;
  /** Delay in milliseconds before hiding overlay with mouseout events. */
  delay?: number;
  /** Wait time in milliseconds before showing the panel with hover events. */
  wait?: number;
  /** If true, keeps overlay open regardless of hover / click state. */
  keepOpen?: boolean;
  /** Add `width: 100%` to the target wrapper element's styling */
  fullWidth?: boolean;
};

/** Accept only camelCase like "top", "bottomStart", "rightEnd". */
function toFloating(p: Placement): FUPlacement {
  const m = /^(top|bottom|left|right)(Start|End)?$/.exec(p);
  if (!m) return "bottom-start";
  // TS can't infer the type from the regex, but we can!
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const side = m[1] as "top" | "bottom" | "left" | "right";
  const align = m[2] ? (m[2] === "Start" ? "start" : "end") : undefined;
  return align ? `${side}-${align}` : side;
}

function isRenderProp(x: ReactNode | OverlayRender): x is OverlayRender {
  return typeof x === "function";
}

export const Overlay = ({
  wait = 0,
  delay = 250,
  event = "hover",
  placement,
  keepOpen = false,
  fullWidth = false,
  target,
  content,
  ...props
}: PopoverProps): ReactElement => {
  const [isOpenState, setIsOpenState] = useState(false);
  const isOpen = keepOpen ? true : isOpenState;

  const preferred = toFloating(placement);
  const GAP = 8; // px gap between target and floating panel

  const { refs, floatingStyles, context } = useFloating({
    placement: preferred,
    strategy: "fixed", // Immune to overflow clipping
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: GAP, crossAxis: 0 }),
      flip({
        padding: 8,
        crossAxis: true,
        altBoundary: true,
        rootBoundary: "viewport",
        fallbackPlacements: ["top", "right", "bottom", "left"],
        fallbackStrategy: "bestFit",
      }),
      shift({ padding: 8 }), // nudge inward from viewport edges
      size({
        padding: 8,
        apply({ availableWidth, availableHeight, elements }) {
          const el = elements.floating;

          // allow a wider tooltip, keep within viewport
          const maxWidth = Math.floor(Math.min(520, availableWidth)) - 1; // was 280
          const maxHeight = Math.floor(Math.min(480, availableHeight)) - 1; // was 360

          Object.assign(el.style, {
            width: "max-content",
            maxWidth: `${maxWidth}px`,
            maxHeight: `${maxHeight}px`,
            overflowY: "visible", // let content manage scrolling if needed
            overflowX: "visible",
          });
        },
      }),
    ],
    open: isOpen,
    onOpenChange: keepOpen ? undefined : setIsOpenState,
  });

  const click = useClick(context, { enabled: event === "click" });
  const hover = useHover(context, {
    enabled: event === "hover",
    delay: { open: wait, close: delay },
    move: true,
  });
  const dismiss = useDismiss(context, { outsidePress: true });
  const role = useRole(context, { role: "menu" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
    role,
  ]);

  const close = () => {
    if (!keepOpen) setIsOpenState(false);
  };

  return (
    <S.Wrapper $fullWidth={fullWidth} {...props}>
      <S.TargetWrapper>
        <S.RefAnchor
          ref={refs.setReference}
          {...getReferenceProps({
            "aria-haspopup": "menu",
            "aria-expanded": isOpen,
          })}
          $fullWidth={fullWidth}
        >
          {target}
        </S.RefAnchor>
      </S.TargetWrapper>

      {isOpen && (
        <FloatingPortal>
          <S.FloatingPanel
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {isRenderProp(content) ? content({ close }) : content}
          </S.FloatingPanel>
        </FloatingPortal>
      )}
    </S.Wrapper>
  );
};
